
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export const useAdmin = () => {
  const { user, isLoggedIn, isLoading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChecked, setHasChecked] = useState(false);

  // 穩定化 user ID 的引用
  const userId = useMemo(() => user?.id, [user?.id]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      console.log('useAdmin: Starting admin check', { userId, isLoggedIn, authLoading });
      
      if (authLoading) {
        console.log('useAdmin: Auth still loading, waiting...');
        return;
      }

      // 防止重複檢查
      if (hasChecked && !userId) {
        console.log('useAdmin: Already checked, no user');
        setIsLoading(false);
        return;
      }

      if (!userId || !isLoggedIn) {
        console.log('useAdmin: No user or not logged in, setting admin to false');
        setIsAdmin(false);
        setIsLoading(false);
        setHasChecked(true);
        return;
      }

      try {
        console.log('useAdmin: Checking admin status for user:', userId);
        
        const { data, error } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', userId)
          .single();

        console.log('useAdmin: Database response:', { data, error });

        if (error) {
          console.error('useAdmin: Error checking admin status:', error);
          setIsAdmin(false);
        } else if (data) {
          const adminStatus = Boolean(data.is_admin);
          console.log('useAdmin: Setting admin status to:', adminStatus);
          setIsAdmin(adminStatus);
        } else {
          console.log('useAdmin: No profile data found, setting admin to false');
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('useAdmin: Unexpected error:', error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
        setHasChecked(true);
        console.log('useAdmin: Admin check completed');
      }
    };

    checkAdminStatus();
  }, [userId, isLoggedIn, authLoading, hasChecked]);

  // 只在開發環境下打印狀態，避免生產環境的性能問題
  if (process.env.NODE_ENV === 'development') {
    console.log('useAdmin: Current state:', { isAdmin, isLoading, userId });
  }

  // 使用 useMemo 穩定返回值
  return useMemo(() => ({ 
    isAdmin, 
    isLoading 
  }), [isAdmin, isLoading]);
};
